import {useEffect, useMemo, useState} from 'react';
import API from "../utils/API";
import {API_ENDPOINTS} from "../apiConfig";
import {useAuth} from "../contexts/AuthContext";

const isValidMode = (mode) => mode === 'table' || mode === 'tiles';

const AdminSectionViewMode = ({section, defaultMode = 'table', availableFieldKeys = [], children}) => {
    const {user, dispatch, isAuthResolved} = useAuth();
    const [viewMode, setViewMode] = useState(defaultMode);
    const [sectionFields, setSectionFields] = useState({table: [], tiles: []});
    const [isReady, setIsReady] = useState(false);
    const [error, setError] = useState(null);

    const availableFieldKeyHash = useMemo(() => availableFieldKeys.join('|'), [availableFieldKeys]);

    useEffect(() => {
        if (!isAuthResolved) return;

        const mode = user?.uiPreferences?.adminSectionView?.[section];
        const fields = user?.uiPreferences?.adminSectionFields?.[section] || {};
        const next = {
            table: normalizeFields(fields.table, availableFieldKeys),
            tiles: normalizeFields(fields.tiles, availableFieldKeys)
        };

        setViewMode(isValidMode(mode) ? mode : defaultMode);
        setSectionFields(next);
        setIsReady(true);
    }, [isAuthResolved, user, section, defaultMode, availableFieldKeyHash]);

    const changeViewMode = async (nextMode) => {
        if (!isValidMode(nextMode) || nextMode === viewMode) return;

        const previousMode = viewMode;
        setViewMode(nextMode);
        setError(null);

        try {
            const response = await API.put(API_ENDPOINTS.user.updateSectionViewMode, {
                section,
                viewMode: nextMode
            });
            if (response?.data) {
                dispatch({type: 'SET_USER', payload: {user: response.data}});
            }
        } catch (err) {
            setViewMode(previousMode);
            setError(err);
        }
    };

    const changeSectionFields = async (mode, nextFields) => {
        if (!['table', 'tiles'].includes(mode)) return;
        const normalizedFields = normalizeFields(nextFields, availableFieldKeys);
        const previous = sectionFields;
        const next = {...sectionFields, [mode]: normalizedFields};
        setSectionFields(next);
        setError(null);

        try {
            const response = await API.put(API_ENDPOINTS.user.updateSectionViewMode, {
                section,
                sectionFields: next
            });
            if (response?.data) {
                dispatch({type: 'SET_USER', payload: {user: response.data}});
            }
        } catch (err) {
            setSectionFields(previous);
            setError(err);
        }
    };

    return children({
        viewMode,
        setViewMode: changeViewMode,
        sectionFields,
        setSectionFields: changeSectionFields,
        isReady: isReady && isAuthResolved,
        error
    });
};

function normalizeFields(fields, allowedKeys = []) {
    const source = Array.isArray(fields) ? fields : [];
    const normalized = source.filter((field) => allowedKeys.includes(field));
    if (normalized.length) return normalized;
    return [...allowedKeys];
}

export default AdminSectionViewMode;
